/**
 * Determines which users the current user is allowed to message.
 *
 * Rules:
 * - Client (Patient): only providers connected through EoC careManager or CareTeam membership.
 * - Provider (Practitioner): same-practice providers (all Practitioners in project for pilot)
 *   plus clients connected through EoC or CareTeam.
 * - Admin (unify-admin role): everyone.
 *
 * Gap: "same practice" currently resolves to all Practitioners in the Medplum project.
 * Production should filter by Organization membership once the app models that relationship.
 */

import type { MedplumClient } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import {
  formatPractitionerPreferredLabel,
  formatPractitionerStaffLabel,
} from '@unify/fhir-model/resources/practitioner';
import {
  loadPractitionerPreferredLabelMap,
  preferredLabelOrFallback,
} from '@/lib/loadPractitionerPreferredLabels';

export interface MessageableRecipient {
  reference: string;
  display: string;
  /** Preferred-only label for FHIR Reference.display writes. */
  preferredDisplay?: string;
  resourceType: 'Patient' | 'Practitioner';
  role?: string;
}

export type MessagingRole = 'client' | 'provider' | 'admin';

/** Detect messaging role from the current Medplum profile. */
export function detectMessagingRole(
  profile: { resourceType: string; extension?: Array<{ url: string; valueString?: string }> },
): MessagingRole {
  if (profile.resourceType === 'Patient' || profile.resourceType === 'RelatedPerson') {
    return 'client';
  }
  const roleExt = profile.extension?.find((e) => e.url === 'https://unify.health/fhir/role');
  if (roleExt?.valueString === 'unify-admin') return 'admin';
  return 'provider';
}

/**
 * Main entry point: resolve messageable recipients for the current user.
 * Returns a deduplicated list of Patient/Practitioner references the user is
 * allowed to start or continue a conversation with.
 */
export async function getMessageableRecipients(
  medplum: MedplumClient,
  profile: { resourceType: string; id?: string; extension?: Array<{ url: string; valueString?: string }> },
): Promise<MessageableRecipient[]> {
  const role = detectMessagingRole(profile);
  const profileId = profile.id ?? '';

  switch (role) {
    case 'client':
      return getRecipientsForClient(medplum, profileId);
    case 'provider':
      return getRecipientsForProvider(medplum, profileId);
    case 'admin':
      return getRecipientsForAdmin(medplum, profileId);
  }
}

// ── Client (Patient) ─────────────────────────────────────────────────────────

async function getRecipientsForClient(
  medplum: MedplumClient,
  patientId: string,
): Promise<MessageableRecipient[]> {
  const providerRefs = new Map<string, string>();

  const eocs = await medplum.searchResources('EpisodeOfCare', {
    patient: `Patient/${patientId}`,
    status: 'active',
  });
  for (const eoc of eocs) {
    if (eoc.careManager?.reference?.startsWith('Practitioner/')) {
      providerRefs.set(eoc.careManager.reference, eoc.careManager.display ?? '');
    }
  }

  try {
    const careTeams = await medplum.searchResources('CareTeam', {
      subject: `Patient/${patientId}`,
      status: 'active',
    });
    for (const ct of careTeams) {
      for (const p of ct.participant ?? []) {
        if (p.member?.reference?.startsWith('Practitioner/')) {
          providerRefs.set(p.member.reference, p.member.display ?? '');
        }
      }
    }
  } catch {
    // CareTeam search may be forbidden until patient policy includes subject=%patient
  }

  const recipients: MessageableRecipient[] = [];
  const labels = await loadPractitionerPreferredLabelMap(
    medplum,
    [...providerRefs.keys()].map((ref) => ref.split('/')[1] ?? ''),
  );
  for (const [ref, display] of providerRefs) {
    const id = ref.split('/')[1];
    recipients.push({
      reference: ref,
      display: preferredLabelOrFallback(id, labels, display, 'Unknown Provider'),
      preferredDisplay: id ? labels.get(id) : undefined,
      resourceType: 'Practitioner',
    });
  }

  return recipients;
}

// ── Provider (Practitioner) ──────────────────────────────────────────────────

async function getRecipientsForProvider(
  medplum: MedplumClient,
  practitionerId: string,
): Promise<MessageableRecipient[]> {
  const recipients: MessageableRecipient[] = [];
  const seen = new Set<string>();

  // Same-practice: all active Practitioners in the project (pilot scope)
  const practitioners = await medplum.searchResources('Practitioner', {
    _count: '100',
    active: 'true',
  });
  for (const p of practitioners) {
    const ref = `Practitioner/${p.id}`;
    if (p.id === practitionerId || seen.has(ref)) continue;
    seen.add(ref);
    recipients.push({
      reference: ref,
      display: formatPractitionerStaffLabel(p) || 'Unknown Provider',
      preferredDisplay: formatPractitionerPreferredLabel(p) || undefined,
      resourceType: 'Practitioner',
      role: 'provider',
    });
  }

  // Connected clients via EoC (care-manager)
  const eocs = await medplum.searchResources('EpisodeOfCare', {
    'care-manager': `Practitioner/${practitionerId}`,
    status: 'active',
  });
  for (const eoc of eocs) {
    const ref = eoc.patient?.reference;
    if (ref?.startsWith('Patient/') && !seen.has(ref)) {
      seen.add(ref);
      recipients.push({
        reference: ref,
        display: eoc.patient?.display ?? 'Unknown',
        resourceType: 'Patient',
        role: 'client',
      });
    }
  }

  // Connected clients via CareTeam membership
  const careTeams = await medplum.searchResources('CareTeam', {
    participant: `Practitioner/${practitionerId}`,
    status: 'active',
  });
  for (const ct of careTeams) {
    const ref = ct.subject?.reference;
    if (ref?.startsWith('Patient/') && !seen.has(ref)) {
      seen.add(ref);
      recipients.push({
        reference: ref,
        display: ct.subject?.display ?? 'Unknown',
        resourceType: 'Patient',
        role: 'client',
      });
    }
  }

  return recipients;
}

// ── Admin ────────────────────────────────────────────────────────────────────

async function getRecipientsForAdmin(
  medplum: MedplumClient,
  ownId: string,
): Promise<MessageableRecipient[]> {
  const recipients: MessageableRecipient[] = [];

  const practitioners = await medplum.searchResources('Practitioner', {
    _count: '100',
    active: 'true',
  });
  for (const p of practitioners) {
    if (p.id === ownId) continue;
    recipients.push({
      reference: `Practitioner/${p.id}`,
      display: formatPractitionerStaffLabel(p) || 'Unknown Provider',
      preferredDisplay: formatPractitionerPreferredLabel(p) || undefined,
      resourceType: 'Practitioner',
      role: 'provider',
    });
  }

  const patients = await medplum.searchResources('Patient', {
    _count: '200',
    active: 'true',
  });
  for (const p of patients) {
    recipients.push({
      reference: `Patient/${p.id}`,
      display: formatPatientName(p),
      resourceType: 'Patient',
      role: 'client',
    });
  }

  return recipients;
}

// ── Validation ───────────────────────────────────────────────────────────────

/** Check whether a user is a participant in a thread. */
export function canAccessThread(
  thread: { recipient?: Array<{ reference?: string }> },
  userReference: string,
): boolean {
  return thread.recipient?.some((r) => r.reference === userReference) ?? false;
}

/** Validate that every recipient is in the eligible set. */
export function canSendToRecipients(
  recipientRefs: string[],
  eligibleRefs: string[],
): boolean {
  const eligible = new Set(eligibleRefs);
  return recipientRefs.every((r) => eligible.has(r));
}

// ── Name formatting ──────────────────────────────────────────────────────────

function formatPatientName(p: Patient): string {
  const name = p.name?.[0];
  if (!name) return 'Unknown';
  if (name.text) return name.text;
  const parts = [...(name.given ?? []), name.family].filter(Boolean);
  return parts.join(' ') || 'Unknown';
}
